/**
 * FL-1 — Field-link technician identity (self-reported).
 *
 * A field-link visitor has no account; the backend requires an
 * `X-SL-Field-Actor` header on every field write so changes stay
 * attributed. The name is remembered per DEVICE per LINK (deliberate
 * client-storage scope: a credential-less visitor has no user/site prefs —
 * localStorage keyed by share-link id is the only sensible home, and two
 * techs sharing a phone via different links don't collide).
 *
 * An in-memory fallback covers private-browsing / storage-denied contexts:
 * the name then lives for the tab session only, which is enough for the
 * write path to keep working (the prompt simply reappears next visit).
 * NOTE: apiFetch percent-encodes the value before putting it in the header
 * (header values are ByteString; Hebrew names would otherwise throw).
 */

export const FIELD_ACTOR_HEADER = 'X-SL-Field-Actor';

const KEY_PREFIX = 'sl-field-actor:';

/** Tab-session fallback when localStorage is unavailable (private mode). */
const memoryFallback = new Map<string, string>();

export function getFieldActor(linkId: string): string | null {
  const mem = memoryFallback.get(linkId);
  if (mem) return mem;
  try {
    const v = localStorage.getItem(KEY_PREFIX + linkId);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function setFieldActor(linkId: string, name: string): void {
  const trimmed = name.trim().slice(0, 60);
  if (!trimmed) return;
  memoryFallback.set(linkId, trimmed);
  try {
    localStorage.setItem(KEY_PREFIX + linkId, trimmed);
  } catch {
    // Storage denied (private mode) — the in-memory copy above keeps this
    // tab working; the name prompt reappears on the next visit.
  }
}
